import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/candidate/authService';

const CandidateAuthContext = createContext(null);

export const useCandidateAuth = () => {
  const context = useContext(CandidateAuthContext);
  if (!context) {
    throw new Error('useCandidateAuth must be used within a CandidateAuthProvider');    
  }
  return context;
};

export const CandidateAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // First check if we have a token from callback URL
      const tokenFromUrl = authService.handleCallback();
      
      if (tokenFromUrl) {
        // Token was in URL, fetch user data
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
          
          // Redirect to stored path or default
          const redirectPath = authService.getRedirectPath();
          authService.clearRedirectPath();
          window.location.href = redirectPath;
        } catch (error) {
          console.error('Auth initialization error:', error);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        // Check existing session
        const currentUser = authService.getStoredUser();
        const token = authService.getToken();
        
        if (currentUser && token) {
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initializeAuth();

    // Listen for storage changes (for multi-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'candidate_token' || e.key === 'candidate_user') {
        const currentUser = authService.getStoredUser();
        const token = authService.getToken();
        
        if (currentUser && token) {
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Login function - initiates Google OAuth
  const login = () => {
    authService.initiateGoogleLogin();
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if profile is completed
  const isProfileCompleted = () => {
    return user?.profileCompleted || false;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    isProfileCompleted
  };

  return (
    <CandidateAuthContext.Provider value={value}>
      {children}
    </CandidateAuthContext.Provider>
  );
};